/* eslint-disable camelcase */
import { Button, Row, useButtonConfirmDialog, useForm } from '@rocketmakers/armstrong';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  DeviceType,
  HeatingZoneID,
  HeatPumpHeatingControl,
  PhysicalLocationID,
} from '../../../../shared/api-clients/_api';
import { FilterSelect } from '../../../../shared/components/filter-select';
import { enumToArray } from '../../../../shared/helpers/enumHelpers';
import { useValidatedForm } from '../../../../shared/hooks/formValidationHook';
import { InputValidator } from '../../../../shared/hooks/ValidationTypes';
import { BackButton } from '../../components/buttons/back-button';
import { SelectInstallLocation } from '../../components/select-install-location';
import { SelectZone } from '../../components/select-zone';
import { Spinner } from '../../components/spinner/spinner';
import { useBeeConfig } from '../../hooks/beeConfig';
import { useZones } from '../../hooks/zones';
import './configure-heat-pump-heating-zone.scss';
import { useConfigureHeatPumpHeatingZone } from './useConfigureHeatPumpHeatingZone';
import { makeIdForDevice } from './utils';

const controlTypeOptions = Object.values(HeatPumpHeatingControl).map((control, i) => ({
  id: control,
  name: `Heat pump zone ${i + 1}`,
}));

interface IHeatingZoneForm {
  heatingZoneID: HeatingZoneID;
  temperatureDial: string;
  temperatureSensor: string;
  control: string;
  temperatureSensorLocation: PhysicalLocationID;
}

const validators: InputValidator<IHeatingZoneForm>[] = [
  {
    key: 'heatingZoneID',
    validators: [
      {
        validate: value => enumToArray(HeatingZoneID).includes(value),
        message: 'Must be a valid heating zone',
      },
    ],
  },
  {
    key: 'temperatureDial',
    validators: [
      {
        validate: value => value?.split(' ').includes(DeviceType.WRF06),
        message: 'Must be a valid temperature dial',
      },
    ],
  },
  {
    key: 'temperatureSensor',
    validators: [
      {
        validate: value => value && Object.values(HeatPumpHeatingControl).includes(value),
        message: 'Must be a valid temperature sensor',
      },
    ],
  },
  {
    key: 'temperatureSensorLocation',
    validators: [
      {
        validate: value => value && Object.values(PhysicalLocationID).includes(value),
        message: 'Must be a valid location',
      },
    ],
  },
  {
    key: 'control',
    validators: [
      {
        validate: (value, dataBinder) =>
          value &&
          Object.values(HeatPumpHeatingControl).includes(value) &&
          dataBinder.getValue('temperatureSensor') === value,
        message: 'Control and sensor must be the same',
      },
    ],
  },
];

const initialState: IHeatingZoneForm = {
  heatingZoneID: null,
  temperatureDial: null,
  temperatureSensor: null,
  control: null,
  temperatureSensorLocation: null,
};

export const ConfigureHeatPumpHeatingZoneView: React.FunctionComponent = () => {
  const { heatingZone } = useParams<{
    heatingZone: HeatingZoneID;
    heatPumpType: string;
  }>();
  const { loading } = useBeeConfig();
  const { getZoneDescription } = useZones();

  const {
    handleSave,
    handleEdit,
    handleDelete,
    currentDevices,
    temperatureDialOptions,
    temperatureSensorOptions,
    assignedZones,
    assignedHeatPumps,
    assignedDials,
    assignedSensors,
    assignedLocations,
  } = useConfigureHeatPumpHeatingZone(getZoneDescription);

  const initState: IHeatingZoneForm = React.useMemo(() => {
    return currentDevices
      ? {
          heatingZoneID: heatingZone,
          temperatureDial: currentDevices.tempDial && makeIdForDevice(currentDevices.tempDial),
          temperatureSensor: currentDevices.tempSensor?.params.heat_pump_heating_control,
          temperatureSensorLocation: currentDevices.tempSensor.params.install_location,
          control: currentDevices.heatPump.params.heat_pump_heating_control,
        }
      : initialState;
  }, [currentDevices]);

  const { DataForm, bind, dataBinder, notifyChange } = useForm<IHeatingZoneForm>(initState);
  const { setTouched, formIsValid, validationErrors } = useValidatedForm<IHeatingZoneForm>(validators, dataBinder);

  React.useEffect(() => {
    dataBinder.setKeyValue('control', dataBinder.getValue('temperatureSensor'));
    notifyChange(); // Notify change to update the form
  }, [dataBinder.getValue('temperatureSensor')]);

  const deletable = useButtonConfirmDialog({
    content: `Are you sure you want to delete the ${getZoneDescription(heatingZone)} Heating Zone?`,
    confirmText: 'Confirm',
    cancelText: 'Cancel',
  });

  return (
    <DataForm validationResults={validationErrors} validationMode="both">
      {loading && <Spinner />}
      <BackButton />
      <h1>
        Configuration <span className="color-primary">Heat Pump Heating Zone</span>
      </h1>
      <div>
        <SelectZone
          {...bind.select('heatingZoneID')}
          label="Where is the heating zone?"
          filterOptions={assignedZones.filter(zone => zone !== heatingZone)}
          onBlur={setTouched}
          data-qa="select-heating-zone-type"
        />
      </div>
      <div>
        <FilterSelect
          {...bind.select('temperatureDial')}
          label="Which temperature dial is in this zone?"
          filterOptions={assignedDials.filter(
            dial => !currentDevices || dial !== makeIdForDevice(currentDevices.tempDial)
          )}
          options={temperatureDialOptions}
          onBlur={setTouched}
          data-qa="select-temperature-dial-type"
        />
      </div>
      <div>
        <FilterSelect
          {...bind.select('temperatureSensor')}
          label="Which temperature sensor is used for this zone?"
          filterOptions={assignedSensors.filter(
            sensor => !currentDevices || sensor !== currentDevices.tempSensor.params.heat_pump_heating_control
          )}
          options={temperatureSensorOptions}
          onBlur={setTouched}
          data-qa="select-temperature-sensor-type"
        />
      </div>
      <div>
        <SelectInstallLocation
          {...bind.select('temperatureSensorLocation')}
          label="Where is the device installed?"
          filterOptions={assignedLocations}
          data-qa="select-temp-sensor-location"
          onBlur={setTouched}
        />
      </div>
      <div>
        <FilterSelect
          {...bind.select('control')}
          label="Which heat pump zone is linked to this zone?"
          filterOptions={assignedHeatPumps.filter(
            heatPump => !currentDevices || heatPump !== currentDevices.heatPump.params.heat_pump_heating_control
          )}
          options={controlTypeOptions}
          onBlur={setTouched}
          disabled={true}
          data-qa="select-control-type"
        />
      </div>
      {heatingZone && (
        <Button
          className="btn-full secondary-button"
          onClick={async () => {
            if (await deletable()) {
              handleDelete();
            }
          }}
          data-qa="delete-button"
        >
          Delete Heating Zone
        </Button>
      )}
      <Row className="btn-row">
        <Button
          className="btn-full inline-meters-button continue-button"
          onClick={heatingZone ? () => handleEdit(dataBinder) : () => handleSave(dataBinder)}
          disabled={!formIsValid}
          data-qa="continue-button"
        >
          Save
        </Button>
      </Row>
    </DataForm>
  );
};
